'use client';

import { useSearchParams, useRouter, usePathname } from 'next/navigation';

import { PlayerDetailsPublicPageFilters } from '@/components/molecules/PlayerDetailsPublicPageFilters';
import { TeamInfoSponsorsListSkeleton } from '@/components/molecules/skeletons/TeamInfoSponsorsListSkeleton';
import { TeamInfoSponsorsList } from '@/components/molecules/team-details/TeamInfoSponsorsList';
import { usePlayerSponsorsQuery } from '@/hooks/player-details/usePlayerSponsorsQuery';

interface PlayerDetailsLayoutClientProps {
	playerId: string;
}

const filterConfig: Record<string, { showSeason: boolean; showTeam: boolean }> = {
	stats: { showSeason: false, showTeam: false },
	results: { showSeason: true, showTeam: false },
	'player-teams': { showSeason: true, showTeam: false },
	schedule: { showSeason: true, showTeam: true }
};

const pagesWithSponsors = ['stats', 'results', 'teams'];

export function PlayerDetailsLayoutClient({ playerId }: PlayerDetailsLayoutClientProps) {
	const searchParams = useSearchParams();
	const router = useRouter();

	const pathname = usePathname();
	const segments = pathname.split('/');
	const pageRoute = segments[segments.length - 1] || '';

	const { showSeason, showTeam } = filterConfig[pageRoute] ?? { showSeason: false, showTeam: false };

	const showSponsorsContent = pagesWithSponsors.includes(pageRoute);
	const { data: sponsorsData, isLoading: isLoadingSponsors } = usePlayerSponsorsQuery({ playerUuid: playerId, isEnabled: showSponsorsContent });

	if (isLoadingSponsors) {
		return <TeamInfoSponsorsListSkeleton />;
	}

	const initialSeason = searchParams?.get('season') || '';
	const initialTeam = searchParams?.get('team') || '';

	const handleFilterChange = (key: string, value: string | null) => {
		const params = new URLSearchParams(searchParams.toString());
		params.delete('current_page');

		if (value) {
			params.set(key, value);
		} else {
			params.delete(key);
		}

		if (key === 'season') {
			params.delete('team');
			params.delete('month');
			params.delete('startDate');
			params.delete('endDate');
		}

		router.replace(`?${params.toString()}`, { scroll: false });
	};

	return (
		<>
			{showSponsorsContent ? <TeamInfoSponsorsList sponsors={sponsorsData?.data?.sponsors || []} /> : null}
			<PlayerDetailsPublicPageFilters
				showSeasonFilter={showSeason}
				showTeamFilter={showTeam}
				seasonValue={initialSeason}
				teamValue={initialTeam}
				onSeasonChange={(opt) => handleFilterChange('season', opt)}
				onTeamChange={(opt) => handleFilterChange('team', opt)}
			/>
		</>
	);
}
